import React, { useEffect, useState, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ClassContext } from "../../context/ClassContext";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import "stream-chat-react/dist/css/v2/index.css";
import "./VideoCallStudent.css";

import { streamClient, streamVideoClient } from "../../config/stream";
import { doc, updateDoc, getDoc, Timestamp, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../../firebaseConfig";

// Import hooks and utilities
import {
  useVideoCall,
  useChatChannel,
  useBreakoutRooms,
  useClassData,
  getUserFromSession,
  Modal,
  LoadingSpinner,
} from "../../hooks/useVideoCall";

import { CopyPlus, Users, LogOut } from "lucide-react";
import EnhancedCallPreview from "../../components/CallPreview";

const VideoCallStudent = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const urlClassId = params.get("classId");
  const { selectedClassId, setSelectedClassId } = useContext(ClassContext);

  // Use classId from URL if present, else from context
  const classId = urlClassId || selectedClassId;

  useEffect(() => {
    if (urlClassId && urlClassId !== selectedClassId) {
      setSelectedClassId(urlClassId);
    }
  }, [urlClassId, selectedClassId, setSelectedClassId]);

  const user = getUserFromSession();
  const [activeRoomId, setActiveRoomId] = useState(null);

  // UI state for breakout rooms
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [numRooms, setNumRooms] = useState(2);
  const [roomDuration, setRoomDuration] = useState(15);
  const [availableSlots, setAvailableSlots] = useState(5);

  const videoContainerRef = useRef(null);

  // Fetch class data
  const { classData } = useClassData(classId);

  // Initialize active room ID when class data is loaded
  useEffect(() => {
    if (classData && classId) {
          setActiveRoomId(classId);
    }
  }, [classData, classId]);

  // Breakout rooms management
  const {
    breakoutRooms,
    hasBreakoutPermission,
    isCreating,
    fetchBreakoutRooms,
    createBreakoutRooms,
  } = useBreakoutRooms(classId);

  // Chat channel management
  const { chatChannel } = useChatChannel({
    classData,
    classId,
    activeRoomId,
    breakoutRooms,
    user,
  });

  const navigate = useNavigate();

  // Handle navigation when call is left
  const handleCallLeft = () => {
    // Check if this window was opened in a new tab (student case)
    // window.opener exists if the page was opened via window.open()
    if (window.opener && !window.opener.closed) {
      // Close this tab/window since it was opened in a new tab
      window.close();
    } else {
      // If not opened in a new tab, navigate back to class details
      if (classId) {
        navigate(`/classDetailsUser/${classId}`);
      } else {
        navigate(-1); // Go back in history
      }
    }
  };

  // Video call management
  const {
    currentCall,
    isCallJoined,
    isLoading,
    loadingMessage,
    isLeaving,
    joinRoom,
    leaveCall,
  } = useVideoCall({
    classId,
    classData,
    activeRoomId,
    user,
    onCallJoined: (call) => {
      // Update active room ID when call is joined
      if (call.id === classId) {
        setActiveRoomId(classId);
      }
    },
    onCallLeft: handleCallLeft,
  });

  // Use a ref to prevent multiple simultaneous join attempts
  const isJoiningRef = useRef(false);
  
  useEffect(() => {
    // Only join if we have a classId, haven't joined yet, and aren't already joining
    // Note: isLoading is managed by the joinRoom function, so we don't check it here
    if (classId && !isCallJoined && !isJoiningRef.current) {
      isJoiningRef.current = true;
      joinRoom(classId)
        .then(() => {
          isJoiningRef.current = false;
        })
        .catch((err) => {
          console.error("[VideoCall] Error auto-joining room:", err);
          isJoiningRef.current = false;
        });
    }

    return () => {
      // Cleanup on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, isCallJoined]);

  // Update room members in Firestore
  const updateRoomMembers = async (roomId, isJoining = true) => {
    try {
      const breakoutRoomRef = doc(
        db,
        "conference_calls",
        classId,
        "breakout_rooms",
        roomId,
      );

      const roomDoc = await getDoc(breakoutRoomRef);
      if (!roomDoc.exists()) return;

      await updateDoc(breakoutRoomRef, {
        roomMembers: isJoining
          ? arrayUnion(user.uid)
          : arrayRemove(user.uid),
      });

      await fetchBreakoutRooms();
    } catch (error) {
      console.error("Error updating room members:", error);
    }
  };

  // Handle creating breakout rooms
  const handleCreateBreakoutRooms = async () => {
    try {
      await createBreakoutRooms(numRooms, roomDuration, availableSlots);
      setIsModalOpen(false);
      setIsViewModalOpen(true);
    } catch (error) {
      console.error("Error creating breakout rooms:", error);
      alert("Failed to create breakout rooms. Please try again.");
    }
  };

  // Join a breakout room
  const handleJoinBreakoutRoom = async (room) => {
    // Check if room is full
    if (room.roomMembers?.length >= room.availableSlots) {
      alert("This breakout room is full.");
      return;
    }

    // Initialize room start time if not started
    if (!room.startedAt) {
      const startedAt = Timestamp.now();
      const classEndTime = Timestamp.fromDate(
        new Date(startedAt.toDate().getTime() + room.roomDuration * 60 * 1000),
      );

      const breakoutRoomRef = doc(
        db,
        "conference_calls",
        classId,
        "breakout_rooms",
        room.id,
      );

      await updateDoc(breakoutRoomRef, {
        startedAt,
        classEndTime,
      });
    }

    // Add user to room members
    await updateRoomMembers(room.id, true);

    // Join the room
    await joinRoom(room.id);
    setActiveRoomId(room.id);
    setIsViewModalOpen(false);

    // Set up auto-return timer if room has an end time
    if (room.classEndTime) {
      const now = new Date();
      const endTime = room.classEndTime.toDate();
      const remainingMs = endTime - now;

      if (remainingMs > 0) {
        setTimeout(() => {
          joinMainClass();
        }, remainingMs);
      }
    }
  };

  // Return to main room
  const joinMainClass = async () => {
    // If in a breakout room, remove from members
    if (activeRoomId !== classId) {
      await updateRoomMembers(activeRoomId, false);
    }

    // Join main room
    await joinRoom(classId);
    setActiveRoomId(classId);
  };

  return (
    <div className="video-call-student" ref={videoContainerRef}>
      {currentCall && !isLoading && !isLeaving ? (
        <>
          <EnhancedCallPreview
            streamVideoClient={streamVideoClient}
            currentCall={currentCall}
            chatClient={streamClient}
            chatChannel={chatChannel}
            onLeaveCall={handleCallLeft}
          />

          {/* Room indicator */}
          <div className="fixed left-8 top-8 z-[1000]">
            {activeRoomId !== classId && (
              <div className="rounded-xl bg-green-600 px-4 py-2 text-white">
                Breakout Room{" "}
                {breakoutRooms.findIndex((room) => room.id === activeRoomId) +
                  1}
              </div>
            )}
          </div>

          {/* Floating action buttons */}
          <div className="fixed bottom-4 left-12 z-[1000] flex gap-2">
            {/* Create Breakout Rooms button */}
            {hasBreakoutPermission && activeRoomId === classId && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#313443] text-white shadow-md transition-colors duration-200 hover:bg-[#404352]"
                aria-label="Create Breakout Room"
              >
                <CopyPlus size={20} />
              </button>
            )}

            {/* View Breakout Rooms button */}
            {activeRoomId === classId && (
              <button
                onClick={() => {
                  fetchBreakoutRooms();
                  setIsViewModalOpen(true);
                }}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#313443] text-white shadow-md transition-colors duration-200 hover:bg-[#404352]"
                aria-label="View Breakout Rooms"
              >
                <Users size={20} />
              </button>
            )}

            {/* Return to Main Room button */}
            {activeRoomId !== classId && (
              <button
                onClick={joinMainClass}
                className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#313443] text-white shadow-md transition-colors duration-200 hover:bg-[#404352]"
                aria-label="Return to Main Room"
              >
                <LogOut size={20} />
              </button>
            )}
          </div>
        </>
      ) : (
        <LoadingSpinner message={loadingMessage} />
      )}

      {/* Create Breakout Rooms Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create Breakout Rooms"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Number of Rooms
            </label>
            <div className="slider-container">
              <input
                type="range"
                min="1"
                max="10"
                value={numRooms}
                onChange={(e) => setNumRooms(parseInt(e.target.value))}
                className="w-full"
              />
              <span className="text-sm font-medium">{numRooms}</span>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Room Duration (minutes)
            </label>
            <div className="slider-container">
              <input
                type="range"
                min="5"
                max="60"
              value={roomDuration}
              onChange={(e) => setRoomDuration(parseInt(e.target.value))}
                className="w-full"
              />
              <span className="text-sm font-medium">{roomDuration}</span>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Available Slots per Room
            </label>
            <div className="slider-container">
              <input
                type="range"
                min="2"
                max="10"
                value={availableSlots}
                onChange={(e) => setAvailableSlots(parseInt(e.target.value))}
                className="w-full"
              />
              <span className="text-sm font-medium">{availableSlots}</span>
            </div>
          </div>

            <button
            onClick={handleCreateBreakoutRooms}
              disabled={isCreating}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
            >
            {isCreating ? "Creating..." : "Create Rooms"}
            </button>
        </div>
      </Modal>

      {/* View Breakout Rooms Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Breakout Rooms"
        size="lg"
      >
        <div className="space-y-2">
          {breakoutRooms.length === 0 ? (
            <p className="text-center text-gray-500">No breakout rooms available</p>
          ) : (
            breakoutRooms.map((room) => (
                <div
                  key={room.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                >
                    <div>
                  <h3 className="font-semibold">{room.roomName}</h3>
                  <p className="text-sm text-gray-500">
                    {room.roomMembers?.length || 0} / {room.availableSlots}{" "}
                    members
                  </p>
                    </div>
                    <button
                  onClick={() => handleJoinBreakoutRoom(room)}
                      disabled={
                    room.roomMembers?.length >= room.availableSlots ||
                    room.roomMembers?.includes(user.uid)
                      }
                  className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-gray-400"
                    >
                  {room.roomMembers?.includes(user.uid)
                    ? "Joined"
                    : room.roomMembers?.length >= room.availableSlots
                        ? "Full"
                            : "Join"}
                    </button>
                  </div>
            ))
          )}
        </div>
      </Modal>
    </div>
  );
};

export default VideoCallStudent;
